import { Injectable } from "@angular/core";
import { Observable, from, throwError } from "rxjs";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { LoadingController, Platform } from "@ionic/angular/standalone";
import { HTTPErrorHandler } from "./http-error-handler";
import { StorageService } from "../storage/storage.service";
import { tap, switchMap, catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { CommonService } from "../../common.service";
import { AppStateService } from "../../init/app-state.service";
import { TokenRelatedService } from "../tokenRelated/token-related.service";
import { environment } from 'src/environments/environment.prod';
import { CookieService } from 'ngx-cookie-service';
@Injectable({
  providedIn: "root"
})
export class HttpInterceptorService implements HttpInterceptor {
  isLoading: boolean;
  numbers: any;
  timer: number = 1;
  checkUUID: number;
  private pendingRequests: Array<{request: HttpRequest<any>, next: HttpHandler}> = [];
  private isRefreshingCSRF = false;
  constructor(
    private loader: LoadingController,
    private platform: Platform,
    private errorHandler: HTTPErrorHandler,
    public commonServ: CommonService,
    public appState: AppStateService,
    public storageService: StorageService,
    public router: Router,
    public tokenRelatedService: TokenRelatedService,
    private cookieService: CookieService,
  ) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Handle UUID generation
    if (this.commonServ.uuid == undefined || null) {
      this.checkUUID = Math.floor(1000000000 * Math.random());
      if (!this.cookieService.get('uuid')) {
        this.commonServ.uuid = 'webUUID' + this.checkUUID;
        this.cookieService.set('uuid', this.commonServ.uuid, 90);
      }
    }

    // Skip interceptor for i18n assets
    if (request.url.indexOf("assets/i18n") !== -1) {
      return next.handle(request);
    }

    // Skip interceptor if explicitly requested
    if (request.headers.get("skip")) {
      return next.handle(request);
    }

    // Process request with proper appData handling
    return this.processRequestWithAppData(request, next);
  }

  private processRequestWithAppData(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Check if we need to wait for appData to be loaded
    const needsAppData = this.requiresAppData(request.url);
    
    if (needsAppData) {
      return from(this.ensureAppDataLoaded()).pipe(
        switchMap(() => this.executeRequest(request, next)),
        catchError((error) => {
          console.error('Failed to load appData:', error);
          return this.handleRequestError(request, next, error);
        })
      );
    } else {
      return this.executeRequest(request, next);
    }
  }

  private async ensureAppDataLoaded(): Promise<void> {
    // Use the async method to ensure data is loaded
    await this.appState.getAppDataAsync();
  }

  private requiresAppData(url: string): boolean {
    // Skip appData requirement for certain endpoints
    const skipAppDataEndpoints = [
      'anonymouskey',
      'assets/',
      'stage.clubmarriott.in',
      'validateSignupPromocode'
    ];
    
    return !skipAppDataEndpoints.some(endpoint => url.includes(endpoint));
  }

  private executeRequest(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let reqHeader = this.commonServ.getRequestHeaders();
    
    // Handle special content types
    if (request.url.indexOf('userImage') !== -1) {
      reqHeader['Content-Type'] = "multipart/form-data; boundary=+++++";
    }

    // Handle bypass session
    if (localStorage.getItem(environment?.headers?.program_id + ":" + "bypassSession") === "true" && 
        request.url.includes("api/validateSignupPromocode")) {
      console.log("not logged in to call api");
      reqHeader['bypassSession'] = "true";
    }

    // Handle pkpass requests
    if ((request.url.indexOf("pkpass") !== -1) && this.router.url === '/table-reserve') {
      console.log("pkpass from interceptor");
      reqHeader['cardtype'] = "reservation";
      reqHeader['program'] = environment.headers.program_id;
      reqHeader['sub_program_id'] = environment.headers.subprogramid;
    }

    // Handle Google Wallet pass generation
    if (request.url.indexOf("generatePassForGoogleWallet") !== -1) {
      console.log("generatePassForGoogleWallet from interceptor");
      if (this.router.url === '/table-reserve') {
        reqHeader['cardtype'] = "reservation";
      } else {
        reqHeader['cardtype'] = "membership";
      }
      reqHeader['program'] = environment.headers.program_id;
    }

    const headers = reqHeader;
    const appData = this.appState.getAppData();
    console.log('AppStateData_HttpInterceptor_Service ==> ', (appData), request.url);

    // Add user session details
    if (appData && appData.userDetails) {
      headers["SessionIDToken"] = appData.userDetails.sessionid || "";
      headers["memberid"] = appData.userDetails.memberid;
    }

    if (appData && appData.csrftoken && appData.csrftoken.csrftoken) {
      headers['csrftoken'] = appData.csrftoken.csrftoken;
    }

    // Add device token for anonymous key requests
    if (request.url.indexOf("anonymouskey") !== -1 && appData && appData.deviceToken) {
      // headers["device_token"] = appData.deviceToken.deviceToken;
    }

    // Add device type
    if (this.platform.is('ios')) {
      headers['device_type'] = 'IOS';
    } else {
      headers['device_type'] = 'ANDROID';
      headers['X-Requested-With'] = environment.bundle_Id;
    }

    if (request.url.includes("/initiatepayment")) {
      headers["source"] = "GC-Web"
    }

    let reqHeaders = {};
    if (request.url.indexOf("stage.clubmarriott.in") === -1) {
      reqHeaders = headers;
    }

    const updatedRequest = request.clone({
      headers: new HttpHeaders(reqHeaders)
    });

    console.log("updatedRequest ==>", headers);
    
    return next.handle(updatedRequest).pipe(
      tap(
        (event: HttpEvent<any>) => {
          // Handle successful response
        },
        (err: any) => {
          if (err instanceof HttpErrorResponse) {
            this.errorHandler.handleError(err);
          }
        }
      ),
      catchError((error: HttpErrorResponse) => {
        return this.handleRequestError(request, next, error);
      })
    );
  }

  private async getCSRFTokenFromStorage(): Promise<string | null> {
    try {
      const csrfData = await this.storageService.get('csrftoken', 'anonymous');
      if (csrfData) {
        const parsed = typeof csrfData === 'string' ? JSON.parse(csrfData) : csrfData;
        return parsed?.csrftoken || null;
      }
    } catch (error) {
      console.error('Error getting CSRF token from storage:', error);
    }
    return null;
  }

  private handleRequestError(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    // Handle CSRF token errors with retry ONLY for TLC-180
    if (error.status === 401 || error.status === 403) {
      let errorCode: string | undefined;
      try {
        if (typeof error.error === 'object') {
          errorCode = error.error?.errorCode;
        } else if (typeof error.error === 'string') {
          const parsed = JSON.parse(error.error || '{}');
          errorCode = parsed?.errorCode;
        }
      } catch (_) {
        // ignore JSON parse errors
      }

      // Refresh CSRF only when invalid CSRF token error is returned
      if (errorCode === 'TLC-180') {
        console.log('TLC-180 detected, attempting CSRF refresh and retry');
        return this.refreshCSRFAndRetry(request, next);
      }

      // Do NOT attempt CSRF refresh on session expired or other errors
      if (errorCode === 'TLC-043') {
        console.log('TLC-043 (session expired) detected, skipping CSRF refresh');
      }
    }
    return throwError(error);
  }

  private refreshCSRFAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshingCSRF) {
      // Queue the request if already refreshing
      this.pendingRequests.push({ request, next });
      return new Observable(observer => {
        // This will be resolved when CSRF refresh completes
        const checkInterval = setInterval(() => {
          if (!this.isRefreshingCSRF) {
            clearInterval(checkInterval);
            this.executeRequest(request, next).subscribe(observer);
          }
        }, 100);
      });
    }

    this.isRefreshingCSRF = true;
    
    return from(this.tokenRelatedService.anaymousApiHit()).pipe(
      switchMap((response) => {
        if (response && response.headers) {
          const csrfToken = response.headers.get('csrftoken');
          if (csrfToken) {
            this.appState.saveCsrftoken({ csrftoken: csrfToken });
            console.log('CSRF token refreshed successfully');
          }
        }
        this.isRefreshingCSRF = false;
        
        // Process pending requests
        this.processPendingRequests();
        
        return this.executeRequest(request, next);
      }),
      catchError((error) => {
        console.error('Failed to refresh CSRF token:', error);
        this.isRefreshingCSRF = false;
        this.processPendingRequests();
        this.errorHandler.handleError(error);
        return throwError(error);
      })
    );
  }

  private processPendingRequests(): void {
    const requests = [...this.pendingRequests];
    this.pendingRequests = [];
    
    requests.forEach(({ request, next }) => {
      this.executeRequest(request, next).subscribe();
    });
  }

  // Loader Based Functionality
  async presentLoader() {
    this.isLoading = true;
    return await this.loader
      .create({
        message: "Please wait",
        translucent: true,
        spinner: "bubbles"
      })
      .then(loadingInstance => {
        loadingInstance.present().then(() => {
          // 
          if (!this.isLoading) {
            loadingInstance
              .dismiss()
              .then(() => console.log("abort presenting"));
          }
        });
      });
  }

  async dismissLoader() {
    this.isLoading = false;
    return await this.loader.dismiss().then(() => console.log("dismissed"));
  }

  // inactivityTimerFunc() {
  //   clearInterval(this.numbers);
  //   this.timer = 1;
  //     this.numbers =  setInterval((x)=>{
  //       this.timer ++;
  //       // //console.log(this.timer);
  //       if(this.timer == 1800){
  //       
  //           clearInterval(this.numbers);
  //           this.timer = 1;
  //           this.storageService.clear();
  //           this.router.navigate([""]);
  //         
  //       }
  //     },1000);
  //   }
}
